import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Users, Stethoscope, Building2, FileText,
  Phone, MapPin, ChevronLeft, ChevronRight, Activity, Heart,
  Download, Mail
} from 'lucide-react';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import './Home.css';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

// ── HERO SLIDER ───────────────────────────────────────────
function HeroSlider({ slides, settings: s }) {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const go = useCallback((idx) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(idx);
    setTimeout(() => setAnimating(false), 600);
  }, [animating]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => go((current + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [current, slides.length, go]);

  if (!slides.length) {
    return (
      <section className="hero hero-default">
        <div className="hero-overlay"/>
        <div className="container hero-inner">
          <div className="hero-content">
            <span className="hero-badge">{s.hero_tagline || 'Spital Publik Rajonal'}</span>
            <h1 className="hero-title">{s.site_name || 'Spitali i Përgjithshëm i Prizrenit'}</h1>
            {s.site_subtitle && <p className="hero-sub">{s.site_subtitle}</p>}
            <p className="hero-desc">{s.hero_description || 'Kujdes shëndetësor cilësor për banorët tanë.'}</p>
            <div className="hero-btns">
              <Link to={s.hero_btn1_url || '/departamentet'} className="btn hero-btn-primary">
                {s.hero_btn1_text || 'Departamentet'} <ArrowRight size={16}/>
              </Link>
              <Link to={s.hero_btn2_url || '/kontakti'} className="btn hero-btn-outline">
                {s.hero_btn2_text || 'Kontakti'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="hero-slider">
      {slides.map((slide, i) => (
        <div key={slide.id} className={`slide ${i === current ? 'active' : ''}`}>
          <img src={`${IMG_BASE}${slide.image}`} alt={slide.title || ''} className="slide-bg"/>
          <div className="slide-overlay"/>
          <div className="container slide-content">
            {slide.title    && <h2 className="slide-title">{slide.title}</h2>}
            {slide.subtitle && <p className="slide-subtitle">{slide.subtitle}</p>}
            {slide.button_text && slide.button_url && (
              <Link to={slide.button_url} className="btn hero-btn-primary">
                {slide.button_text} <ArrowRight size={16}/>
              </Link>
            )}
          </div>
        </div>
      ))}
      {slides.length > 1 && (
        <>
          <button className="slider-arrow left"  onClick={() => go((current - 1 + slides.length) % slides.length)}><ChevronLeft size={24}/></button>
          <button className="slider-arrow right" onClick={() => go((current + 1) % slides.length)}><ChevronRight size={24}/></button>
          <div className="slider-dots">
            {slides.map((_, i) => (
              <button key={i} className={`dot ${i === current ? 'active' : ''}`} onClick={() => go(i)}/>
            ))}
          </div>
        </>
      )}
      <div className="slider-info-bar">
        <div className="container slider-info-inner">
          <span className="slider-badge">{s.hero_tagline || 'Spital Publik Rajonal'}</span>
          <span className="slider-site-name">{s.site_name || 'Spitali i Prizrenit'}</span>
        </div>
      </div>
    </section>
  );
}

// ── STAT CARD ─────────────────────────────────────────────
function StatCard({ value, label, icon: Icon, color }) {
  return (
    <div className="stat-card" style={{'--sc': color}}>
      <div className="stat-icon-wrap"><Icon size={20}/></div>
      <div className="stat-value">{Number(value||0).toLocaleString()}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// ── NEWS CARD ─────────────────────────────────────────────
function NewsCard({ item }) {
  return (
    <Link to={`/lajmet/${item.slug}`} className="news-card">
      <div className="news-img-wrap">
        {item.cover_image
          ? <img src={`${IMG_BASE}${item.cover_image}`} alt={item.title}/>
          : <div className="news-img-ph"><Activity size={32} color="#93b4d4"/></div>
        }
        {item.category_name && (
          <span className="nc-badge" style={{background: item.category_color||'#0B4F8A'}}>
            {item.category_name}
          </span>
        )}
      </div>
      <div className="news-body">
        <p className="news-meta">{item.published_at ? new Date(item.published_at).toLocaleDateString('sq-AL',{day:'numeric',month:'long',year:'numeric'}) : ''}</p>
        <h3 className="news-title">{item.title}</h3>
        {item.excerpt && <p className="news-excerpt">{item.excerpt}</p>}
        <span className="news-read-more">Lexo më shumë →</span>
      </div>
    </Link>
  );
}

function formatSize(b) {
  if (!b) return '';
  return b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`;
}

// ── MAIN ──────────────────────────────────────────────────
export default function Home() {
  const s = useSettings();
  const [slides,  setSlides]  = useState([]);
  const [news,    setNews]    = useState([]);
  const [depts,   setDepts]   = useState([]);
  const [pubs,    setPubs]    = useState([]);
  const [director, setDirector] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/slider'),
      api.get('/news?limit=6'),
      api.get('/departments'),
      api.get('/publications?limit=5&status=published'),
      api.get('/leadership?category=udheheqja'),
    ]).then(([sliderR, newsR, deptsR, pubsR, leadR]) => {
      setSlides(sliderR.data);
      setNews(newsR.data.data || []);
      setDepts(deptsR.data);
      setPubs(pubsR.data.data || []);
      // Director = sort_order=1 or first in leadership
      const leaders = leadR.data || [];
      const dir = leaders.find(l => l.sort_order === 1) || leaders[0] || null;
      setDirector(dir);
    }).finally(() => setLoading(false));
  }, []);

  const handleDownload = async (pub) => {
    try {
      const response = await fetch(`${IMG_BASE}/api/publications/${pub.id}/download`);
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = pub.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Gabim gjatë shkarkimit.'); }
  };

  return (
    <>
      <Header/>
      <main>
        {/* SLIDER */}
        <HeroSlider slides={slides} settings={s}/>

        {/* STATS */}
        <section className="stats-bar">
          <div className="container stats-grid">
            <StatCard value={s.staff_count}       label="Të punësuar"          icon={Users}       color="#185FA5"/>
            <StatCard value={s.doctors_count}     label="Mjekë specialistë"    icon={Stethoscope} color="#0A7B55"/>
            <StatCard value={s.nurses_count}      label="Infermierë"           icon={Heart}       color="#7C3AED"/>
            <StatCard value={s.beds_count}        label="Shtretër"             icon={Building2}   color="#C2620A"/>
            <StatCard value={s.population_served} label="Popullata e mbuluar"  icon={Users}       color="#0B7696"/>
          </div>
        </section>

        {/* QUICK ACCESS */}
        <section className="quick-section">
          <div className="container">
            <div className="quick-grid">
              {[
                { icon: <Stethoscope size={24}/>, label: 'Departamentet', sub: `${depts.length} departamente`, to: '/departamentet', color: 'var(--blue-700)' },
                { icon: <Activity size={24}/>,    label: 'Shërbimet',     sub: 'Shërbime spitalore',          to: '/sherbimet',    color: '#0A7B55' },
                { icon: <FileText size={24}/>,    label: 'Publikimet',    sub: 'Raporte & dokumente',          to: '/publikimet',   color: '#7C3AED' },
                { icon: <MapPin size={24}/>,      label: 'Si të vini',    sub: 'Harta & udhëzime',             to: '/si-te-vini',   color: '#C2620A' },
                { icon: <Phone size={24}/>,       label: 'Kontakti',      sub: s.emergency_phone||'+383...',   to: '/kontakti',     color: '#B91C1C' },
              ].map(q => (
                <Link key={q.to} to={q.to} className="quick-card" style={{'--qc': q.color}}>
                  <div className="quick-icon-wrap">{q.icon}</div>
                  <div className="quick-info">
                    <p className="quick-label">{q.label}</p>
                    <span className="quick-sub">{q.sub}</span>
                  </div>
                  <ArrowRight size={15} className="quick-arrow"/>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* NEWS */}
        <section className="home-section">
          <div className="container">
            <div className="section-header">
              <div>
                <span className="section-eyebrow">Informohuni</span>
                <h2 className="section-title">Lajmet e fundit</h2>
              </div>
              <Link to="/lajmet" className="btn btn-outline-blue">Të gjitha lajmet →</Link>
            </div>
            {loading ? <div className="spinner"/> : (
              news.length
                ? <div className="news-grid">{news.map(n => <NewsCard key={n.id} item={n}/>)}</div>
                : <div className="empty-box">Nuk ka lajme për momentin.</div>
            )}
          </div>
        </section>

        {/* DIRECTOR + PUBLICATIONS — two-column section */}
        {!loading && (director || pubs.length > 0) && (
          <section className="home-section dir-pub-section">
            <div className="container">
              <div className="dir-pub-grid">

                {/* Director card */}
                {director && (
                  <div className="dir-card card">
                    <div className="dir-card-top">
                      <span className="dir-eyebrow">Udhëheqja</span>
                      <h2 className="dir-section-title">Drejtori i Spitalit</h2>
                    </div>
                    <div className="dir-profile">
                      <div className="dir-photo-wrap">
                        {director.photo
                          ? <img src={`${IMG_BASE}${director.photo}`} alt={director.name} className="dir-photo"/>
                          : <div className="dir-photo-placeholder">{director.name.charAt(0)}</div>
                        }
                        {/* <div className="dir-photo-badge">Nr. 1</div> */}
                      </div>
                      <div className="dir-info">
                        <h3 className="dir-name">{director.name}</h3>
                        <p className="dir-position">{director.position}</p>
                        {director.bio && <p className="dir-bio">{director.bio}</p>}
                        <div className="dir-contacts">
                          {director.email && (
                            <a href={`mailto:${director.email}`} className="dir-contact">
                              <Mail size={14}/>{director.email}
                            </a>
                          )}
                          {director.phone && (
                            <a href={`tel:${director.phone}`} className="dir-contact">
                              <Phone size={14}/>{director.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <Link to="/udheheqja" className="dir-all-link">
                      Shiko të gjithë udhëheqjen →
                    </Link>
                  </div>
                )}

                {/* Recent publications */}
                {pubs.length > 0 && (
                  <div className="pubs-card card">
                    <div className="pubs-card-top">
                      <div>
                        <span className="dir-eyebrow">Dokumentet zyrtare</span>
                        <h2 className="dir-section-title">Publikimet e fundit</h2>
                      </div>
                      <Link to="/publikimet" className="btn btn-outline-blue btn-sm">Të gjitha →</Link>
                    </div>
                    <div className="pubs-list">
                      {pubs.map(pub => (
                        <div key={pub.id} className="pub-row">
                          <div className="pub-row-icon">
                            {pub.file_type === 'application/pdf' ? '📄' : '📁'}
                          </div>
                          <div className="pub-row-info">
                            <p className="pub-row-title">{pub.title}</p>
                            <div className="pub-row-meta">
                              {pub.type_name && <span>{pub.type_name}</span>}
                              {pub.year      && <span>{pub.year}</span>}
                            </div>
                          </div>
                          {pub.file_path && !pub.file_path.includes('placeholder') ? (
                            <button
                              className="btn btn-secondary btn-sm pub-dl-btn"
                              onClick={() => handleDownload(pub)}
                              title="Shkarko"
                            >
                              <Download size={14}/>
                            </button>
                          ) : (
                            <Link to={`/publikimet`} className="pub-row-link" title="Shiko të gjitha publikimet">
                              →
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                    <Link to="/publikimet" className="dir-all-link">
                      Shiko të gjitha publikimet →
                    </Link>
                  </div>
                )}

              </div>
            </div>
          </section>
        )}

        {/* DEPARTMENTS */}
        {!loading && depts.length > 0 && (
          <section className="home-section depts-section">
            <div className="container">
              <div className="section-header">
                <div>
                  <span className="section-eyebrow">Struktura spitalore</span>
                  <h2 className="section-title">Departamentet tona</h2>
                </div>
                <Link to="/departamentet" className="btn btn-outline-blue">Shiko të gjitha →</Link>
              </div>
              <div className="depts-cards">
                {depts.map((d, i) => (
                  <Link key={d.id} to={`/departamentet/${d.slug}`} className="dept-card" style={{'--di':i}}>
                    <div className="dept-card-top">
                      {d.image
                        ? <img src={`${IMG_BASE}${d.image}`} alt={d.name} className="dept-card-img"/>
                        : <div className="dept-card-icon">🏥</div>
                      }
                    </div>
                    <div className="dept-card-body">
                      <h3 className="dept-card-name">{d.name}</h3>
                      {d.description && <p className="dept-card-desc">{d.description}</p>}
                      <span className="dept-card-link">Shiko stafin →</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* EMERGENCY BANNER */}
        <section className="emergency-banner">
          <div className="container emg-inner">
            <div className="emg-left">
              <Phone size={28} className="emg-icon"/>
              <div>
                <p className="emg-label">Emergjenca 24 orë / 7 ditë</p>
                <a href={`tel:${s.emergency_phone}`} className="emg-number">{s.emergency_phone || '+383 45 104 230'}</a>
              </div>
            </div>
            <div className="emg-divider"/>
            {/* <div className="emg-right">
              <p className="emg-label">Linja falas për ankesa</p>
              <a href={`tel:${s.free_line}`} className="emg-free">{s.free_line || '0800 17777'}</a>
            </div> */}
          </div>
        </section>
      </main>
      <Footer/>
    </>
  );
}
